import { useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";

export type TemplateType =
  | "destiny"
  | "color"
  | "element"
  | "birth_day"
  | "expression"
  | "heart_desire"
  | "personality"
  | "karmic_dept"
  | "year_foresight"
  | "pin_code_1"
  | "pin_code_2"
  | "pin_code_3"
  | "pin_code_4"
  | "pin_code_5"
  | "pin_code_6"
  | "pin_code_7"
  | "pin_code_8"
  | "pin_code_9"
  | "pin_genel"
  | "iliski_genel"
  | "iliski_ruh_duygusu"
  | "iliski_element"
  | "iliski_isleme"
  | "email_subject"
  | "email_body";

export type PinGenelMode = "yogunluk" | "dagilim" | "varsayilan";

export type Template = {
  id: string;
  templateType: TemplateType;
  lang: "tr" | "en" | "es" | "de";
  title: string;
  content: string;
  calculatedId: string;
  pinGenelMode?: PinGenelMode;
  pinGenelDominant?: number;
  pinGenelMinCount?: number;
  pinGenelSecondary?: number;
  updatedAt: number;
};

const TEMPLATE_TYPE_LABELS: Record<TemplateType, string> = {
  destiny: "Kader Sayısı",
  color: "Renk",
  element: "Element",
  birth_day: "Doğum Günü",
  expression: "İfade",
  heart_desire: "Kalp Arzusu",
  personality: "Kişilik Numarası",
  karmic_dept: "Karmik Borç",
  year_foresight: "Yılın Öngörüsü",
  pin_code_1: "Pin Kodu 1",
  pin_code_2: "Pin Kodu 2",
  pin_code_3: "Pin Kodu 3",
  pin_code_4: "Pin Kodu 4",
  pin_code_5: "Pin Kodu 5",
  pin_code_6: "Pin Kodu 6",
  pin_code_7: "Pin Kodu 7",
  pin_code_8: "Pin Kodu 8",
  pin_code_9: "Pin Kodu 9",
  pin_genel: "PIN Genel Yorum",
  iliski_genel: "İlişki - Genel",
  iliski_ruh_duygusu: "İlişki - Ruh Duygusu",
  iliski_element: "İlişki - Element Analizi",
  iliski_isleme: "İlişki - İşleme Stili",
  email_subject: "Mail Konu",
  email_body: "Mail İçerik",
};

function getCalculatedIdPlaceholder(templateType: TemplateType) {
  if (templateType === "iliski_genel" || templateType === "iliski_element") return "varsayilan";
  if (templateType === "iliski_ruh_duygusu") return "1-9";
  if (templateType === "iliski_isleme") return "Dengeli / Baskın / Edilgen";
  if (templateType.startsWith("pin_code_")) return "1-9";
  if (templateType === "pin_genel") return "(opsiyonel)";
  if (templateType === "email_subject" || templateType === "email_body") {
    return "order_processing / order_shipped / order_cancelled";
  }
  return "";
}

function getCalculatedIdHelpText(templateType: TemplateType) {
  if (templateType === "email_subject" || templateType === "email_body") {
    return "Sipariş durum mailleri için calculatedId alanına order_processing, order_payment_failed, order_payment_pending, order_preparing, order_shipped, order_delivered veya order_cancelled yazın. Kullanılabilir değişkenler: {{ad_soyad}}, {{order_id}}, {{status_label}}, {{total_amount}}, {{shipping_carrier}}, {{shipping_tracking_number}}, {{payment_url}}, {{orders_url}}.";
  }

  return "Bu alan, hesaplanan sonuç ile şablon eşleşmesi için kullanılır.";
}

type TemplatesTabProps = {
  templates: Template[];
  selectedLang: "tr" | "en" | "es";
  onLangChange: (lang: "tr" | "en" | "es") => void;
  mode: "list" | "edit";
  draft: Template | null;
  editorView: "code" | "preview";
  isSaving: boolean;
  isDeleting: boolean;
  isImporting: boolean;
  error?: string;
  onModeChange: (mode: "list" | "edit") => void;
  onDraftChange: (updater: (prev: Template) => Template) => void;
  onEditorViewChange: (view: "code" | "preview") => void;
  onCreate: () => void;
  onEdit: (template: Template) => void;
  onSave: () => void;
  onDelete: (id: string) => void;
  onImportFile: (file: File) => Promise<void>;
};

const LANG_OPTIONS: { value: "tr" | "en" | "es"; label: string }[] = [
  { value: "tr", label: "TR Turkce" },
  { value: "en", label: "EN English" },
  { value: "es", label: "ES Espanol" },
];

export function TemplatesTab({
  templates,
  selectedLang,
  onLangChange,
  mode,
  draft,
  editorView,
  isSaving,
  isDeleting,
  isImporting,
  error,
  onModeChange,
  onDraftChange,
  onEditorViewChange,
  onCreate,
  onEdit,
  onSave,
  onDelete,
  onImportFile,
}: TemplatesTabProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState("");

  const q = search.trim().toLowerCase();
  const visibleTemplates = q
    ? templates.filter(
        (t) =>
          (TEMPLATE_TYPE_LABELS[t.templateType] ?? t.templateType).toLowerCase().includes(q) ||
          t.calculatedId.toLowerCase().includes(q),
      )
    : templates;

  return (
    <Card className="p-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-sm font-semibold">Şablon Listesi</div>
          <div className="mt-1 text-xs text-muted-foreground">
            Şablonları listeden seçip "Detay" ile düzenleyebilirsin.
          </div>
          {mode === "list" && (
            <div className="mt-3 flex gap-1">
              {LANG_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => onLangChange(opt.value)}
                  style={
                    selectedLang === opt.value
                      ? { backgroundColor: "#674bb5", color: "#fff", borderColor: "#674bb5" }
                      : {}
                  }
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    selectedLang === opt.value
                      ? ""
                      : "border-border bg-background text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {mode === "list" ? (
            <>
              <Button
                variant="outline"
                className="rounded-full"
                disabled={isImporting}
                onClick={() => fileInputRef.current?.click()}
              >
                {isImporting ? "İçe aktarılıyor..." : "JSON İçe Aktar"}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.currentTarget.value = "";
                  if (!file) return;

                  void onImportFile(file).catch((err) => {
                    alert(err instanceof Error ? err.message : "JSON içe aktarma başarısız.");
                  });
                }}
              />
            </>
          ) : null}

          {mode === "edit" ? (
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => {
                onModeChange("list");
                onEditorViewChange("code");
              }}
            >
              Geri
            </Button>
          ) : (
            <Button className="rounded-full" onClick={onCreate}>
              Yeni Şablon
            </Button>
          )}

          {mode === "edit" ? (
            <Button className="rounded-full" disabled={isSaving || !draft} onClick={onSave}>
              {isSaving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="mt-3 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      {mode === "list" && (
        <div className="relative mt-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Sablon tipi veya hesaplanmis sonuc ara..."
            className="pr-8 text-sm"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Temizle"
            >
              x
            </button>
          )}
        </div>
      )}

      <Separator className="my-4" />

      {mode === "list" ? (
        <div className="overflow-x-auto">
          <div className="min-w-[860px]">
            <div className="grid grid-cols-[180px_80px_80px_110px_80px_1fr_180px] gap-2 border-b pb-2 text-xs font-semibold text-muted-foreground">
              <div>Şablon Tipi</div>
              <div>Mod</div>
              <div>Baskın</div>
              <div>Minimum Adet</div>
              <div>İkincil</div>
              <div>Hesaplanmış Sonuç</div>
              <div className="text-right">İşlem</div>
            </div>

            <div className="divide-y">
              {visibleTemplates.map((template) => (
                <div
                  key={template.id}
                  className="grid grid-cols-[180px_80px_80px_110px_80px_1fr_180px] gap-2 py-3 text-sm"
                >
                  <div className="font-medium text-green-700">{TEMPLATE_TYPE_LABELS[template.templateType]}</div>
                  <div className="text-muted-foreground">
                    {template.templateType === "pin_genel" ? (template.pinGenelMode ?? "-") : "-"}
                  </div>
                  <div className="text-muted-foreground">
                    {template.templateType === "pin_genel" && template.pinGenelDominant ? template.pinGenelDominant : "-"}
                  </div>
                  <div className="text-muted-foreground">
                    {template.templateType === "pin_genel" && template.pinGenelMinCount ? template.pinGenelMinCount : "-"}
                  </div>
                  <div className="text-muted-foreground">
                    {template.templateType === "pin_genel" && template.pinGenelSecondary ? template.pinGenelSecondary : "-"}
                  </div>
                  <div className="truncate text-green-700">{template.calculatedId || ""}</div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" className="h-8" onClick={() => onEdit(template)}>
                      Detay
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      disabled={isDeleting}
                      onClick={() => {
                        if (!confirm(`\"${template.title}\" şablonu silinsin mi?`)) return;
                        onDelete(template.id);
                      }}
                    >
                      Sil
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : !draft ? (
        <div className="text-sm text-muted-foreground">Şablon seçiniz.</div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
          <Card className="p-4">
            <div className="text-sm font-semibold">Alanlar</div>

            <div className="mt-3 grid gap-3">
              <div className="grid gap-2">
                <Label>Şablon Tipi</Label>
                <select
                  className="h-10 rounded-md border bg-background px-3 text-sm"
                  value={draft.templateType}
                  onChange={(e) => onDraftChange((prev) => ({ ...prev, templateType: e.target.value as TemplateType }))}
                >
                  {Object.entries(TEMPLATE_TYPE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid gap-2">
                <Label>Hesaplanmış Sonuç</Label>
                <Input
                  value={draft.calculatedId}
                  onChange={(e) => onDraftChange((prev) => ({ ...prev, calculatedId: e.target.value }))}
                  placeholder={getCalculatedIdPlaceholder(draft.templateType)}
                />
                <div className="text-xs text-muted-foreground">{getCalculatedIdHelpText(draft.templateType)}</div>
              </div>

              {draft.templateType === "pin_genel" ? (
                <div className="grid gap-3 rounded-lg border p-3">
                  <div className="text-xs font-semibold text-muted-foreground">PIN Genel filtreleri</div>

                  <div className="grid gap-2">
                    <Label>Mod</Label>
                    <select
                      className="h-10 rounded-md border bg-background px-3 text-sm"
                      value={draft.pinGenelMode ?? "varsayilan"}
                      onChange={(e) => onDraftChange((prev) => ({ ...prev, pinGenelMode: e.target.value as PinGenelMode }))}
                    >
                      <option value="yogunluk">yoğunluk</option>
                      <option value="dagilim">dağılım</option>
                      <option value="varsayilan">varsayılan</option>
                    </select>
                  </div>

                  <div className="grid gap-2">
                    <Label>Baskın</Label>
                    <Input
                      type="number"
                      min={0}
                      max={9}
                      value={draft.pinGenelDominant ?? 0}
                      onChange={(e) =>
                        onDraftChange((prev) => ({ ...prev, pinGenelDominant: Number(e.target.value || 0) }))
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>Minimum Adet</Label>
                    <Input
                      type="number"
                      min={0}
                      max={9}
                      value={draft.pinGenelMinCount ?? 0}
                      onChange={(e) =>
                        onDraftChange((prev) => ({ ...prev, pinGenelMinCount: Number(e.target.value || 0) }))
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>İkincil</Label>
                    <Input
                      type="number"
                      min={0}
                      max={9}
                      value={draft.pinGenelSecondary ?? 0}
                      onChange={(e) =>
                        onDraftChange((prev) => ({ ...prev, pinGenelSecondary: Number(e.target.value || 0) }))
                      }
                    />
                  </div>
                </div>
              ) : null}

              <div className="grid gap-2">
                <Label>Dil</Label>
                <select
                  className="h-10 rounded-md border bg-background px-3 text-sm"
                  value={draft.lang}
                  onChange={(e) => onDraftChange((prev) => ({ ...prev, lang: e.target.value as Template["lang"] }))}
                >
                  <option value="tr">tr</option>
                  <option value="en">en</option>
                  <option value="es">es</option>
                  <option value="de">de</option>
                </select>
              </div>

              <div className="grid gap-2">
                <Label>Başlık</Label>
                <Input value={draft.title} onChange={(e) => onDraftChange((prev) => ({ ...prev, title: e.target.value }))} />
              </div>

              <div className="text-xs text-muted-foreground">
                Değişken örnekleri: <code>{"{{ad_soyad}}"}</code>, <code>{"{{kader_sayisi}}"}</code>, <code>{"{{pin_kodu_1}}"}</code>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-semibold">İçerik</div>
              <div className="flex gap-2">
                <Button
                  variant={editorView === "code" ? "default" : "outline"}
                  size="sm"
                  className="h-8"
                  onClick={() => onEditorViewChange("code")}
                >
                  Kod
                </Button>
                <Button
                  variant={editorView === "preview" ? "default" : "outline"}
                  size="sm"
                  className="h-8"
                  onClick={() => onEditorViewChange("preview")}
                >
                  Görsel
                </Button>
              </div>
            </div>

            <div className="mt-3">
              {editorView === "code" ? (
                <Textarea
                  className="min-h-[360px] font-mono text-xs"
                  value={draft.content}
                  onChange={(e) => onDraftChange((prev) => ({ ...prev, content: e.target.value }))}
                />
              ) : (
                <iframe
                  title="template-preview"
                  className="h-[360px] w-full rounded-lg border bg-white"
                  sandbox=""
                  srcDoc={draft.content}
                />
              )}
            </div>
          </Card>
        </div>
      )}
    </Card>
  );
}
